<template>
    <div v-editable="blok" data-test="NewL4EmailSignup" class="EmailSignup">
        <div :class="Message.error || Message.success ? 'EmailSignup--signUp-message' : 'EmailSignup--signUp'">
            <div class="EmailSignup--container">
                <label class="EmailSignup--label">{{ blok.text }}</label>
                <span class="flex justify-center">
                    <input
                        id="Email"
                        v-model="EmailAddress"
                        name="email-input"
                        type="text"
                        placeholder="Email"
                        class="EmailSignup--input shadow"
                        :readonly="loading"
                    />
                    <button
                        class="EmailSignup--button EmailSignup--handHover py-1 px-2.5 ml-2 text-lg md:h-full min-w-[100px] min-h-[36px]"
                        @click="postSuscribe"
                    >
                        <template v-if="loading">
                            <div class="load"></div>
                        </template>
                        <template v-else>
                            {{ blok.button_label }}
                        </template>
                    </button>
                </span>
            </div>
            <p
                v-if="Message.error || Message.success"
                :class="Message.error ? 'EmailSignup--message--error-message' : 'EmailSignup--message--success-message'"
            >
                {{ Message.error || Message.success }}
            </p>
        </div>
        <NuxtLink
            :to="domain + blok.help_link_url"
            class="sm:w-1/3 h-15 EmailSignup--button EmailSignup--handHover text-2xl"
            external
        >
            <button>{{ blok.help_link_text.toUpperCase() }}</button>
        </NuxtLink>
    </div>
</template>

<script setup lang="ts">
import type { IL4EmailSignup } from '~/types/components/L4EmailSignup';

const props = defineProps<{ blok: IL4EmailSignup }>();

const EmailAddress: Ref<string> = ref('');
const LocationCode = ref(props.blok.location_code);
const SectionName = ref(props.blok.product_category);
const signupSource = ref(props.blok.signupSource);
const Message = reactive<{ error?: string; success?: string }>({});
const { locale, locales } = useI18n();
const localeObj = Object.values(locales.value).find((item: any) => item.code === locale.value);
const domain = useRuntimeConfig().public.SITE_URL;
const listId = useRuntimeConfig().public.ITERABLE_LIST_ID;

// check if it is a valid email
const checkEmail = (value: string): boolean => {
    const regex =
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;
    return regex.test(value);
};

const loading = ref<boolean>(false);

async function postSuscribe() {
    try {
        Message.error = '';
        Message.success = '';
        loading.value = true;

        if (!checkEmail(EmailAddress.value)) {
            Message.error = 'Please enter a valid email address!';
            loading.value = false;
            return;
        }
        const response = await fetch(`/api/subscribe`, {
            method: 'POST', // or 'PUT'
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: EmailAddress.value,
                locationCode: LocationCode.value,
                sectionName: SectionName.value,
                listId,
                culture: localeObj.iso,
                signupSource: signupSource.value ?? '',
            }),
        });

        const result = await response.json();
        if (result.error) {
            Message.error = result.error;
        } else {
            Message.success = 'Successful!';
        }
        loading.value = false;
    } catch (error) {
        console.error('Error:', error);
        loading.value = false;
    }
}
</script>

<style lang="postcss" scoped>
.EmailSignup {
    @apply md:container my-5 md:px-0 font-normal flex flex-col sm:flex-row justify-between;
    &--container {
        @apply flex-row md:flex w-full justify-between px-4 py-2 my-1;
    }
    &--button {
        @apply bg-primary text-white flex items-center justify-center;
    }
    &--label {
        @apply text-primary flex justify-center items-center mb-2 md:mb-0;
    }
    &--handHover:hover {
        @apply bg-handHover;
    }
    &--input {
        @apply border border-[#ddd] px-1 text-[18px] w-full md:w-32;
    }
    &--signUp {
        @apply sm:w-[65%] flex flex-col items-center justify-center bg-white text-[15px] md:px-0;
    }
    &--signUp-message {
        @apply sm:w-[65%] mb-4 md:mb-0 flex flex-col items-center justify-around bg-white text-[15px] px-[2%] md:px-0 pb-1 md:h-24;
    }
    &--message {
        @apply flex justify-center items-center;

        &--error-message {
            @apply h-6 text-red-500 flex justify-center items-center;
        }
        &--success-message {
            @apply h-10 w-[99%] bg-[#A0D545] text-base text-white flex justify-center items-center;
        }
    }
    .shadow {
        box-shadow: inset 0 0 10px #ddd;
    }
    ::placeholder {
        color: #666;
        opacity: 1;
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@-webkit-keyframes rotate {
    from {
        -webkit-transform: rotate(0deg);
    }
    to {
        -webkit-transform: rotate(360deg);
    }
}

.load {
    @apply w-4 h-4 mx-auto border-2 border-white border-r-transparent border-b-transparent rounded-full;
    -webkit-transition: all 0.5s ease-in;
    -webkit-animation-name: rotate;
    -webkit-animation-duration: 1s;
    -webkit-animation-iteration-count: infinite;
    -webkit-animation-timing-function: linear;

    transition: all 0.5s ease-in;
    animation-name: rotate;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
}
</style>
